<script lang="ts" setup>
import { ref, onBeforeUnmount, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import axiosInstance from '@/plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';


const router = useRouter();
const route = useRoute();
const email = ref<string>('');
const firstName = ref<string>('');
const lastName = ref<string>('');
const password = ref<string>('');
const confirmPassword = ref<string>('');
const toast = useToast();
const successModal = ref(false);
const registrationDisabled = ref(false)
const loading = ref(false)
const assetsUrl = import.meta.env.VITE_ADMIN_ASSETS_URL;
const backToLoginTimer = ref(0)
const interval = ref();
const hashCheckerModalVisible = ref(true);
const encryptedHash = ref(route.query.hash as string);
const decryptedHash = ref({
  email: '',
});
const hashTimer = ref(true);
const hashNotValidModal = ref(false);
const errorMessages = ref()
const lockedEmail = ref(false)

const startProgress = () => {
  const increment = 100 / 3;
  interval.value = setInterval(() => {
    backToLoginTimer.value += increment;
    if (backToLoginTimer.value >= 100) {
      backToLoginTimer.value = 100;
      clearInterval(interval.value);
      router.push({ path: '/login' });

    }
  }, 1000);
};


const endProgress = () => {
  clearInterval(interval.value);
  interval.value = null;
};



const register = async () => {
  loading.value = true;
  if (password.value !== confirmPassword.value) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: 'Passwörter stimmen nicht überein', life: 5000 });
    loading.value = false;
    return;
  }
  try {
    const response = await axiosInstance.post('/auth/register', {
      email:  decryptedHash.value.data.email,
      password: password.value,
      firstName: firstName.value,
      lastName: lastName.value,
      hash: encryptedHash.value
    });
    startProgress();
    successModal.value = true;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    toast.add({ severity: 'error', summary: 'Fehler', detail: errorMessage, life: 5000 });
  } finally {
    loading.value = false;
  }
};


const checkInviteHash = async () => {

  if (!encryptedHash.value) {
    hashCheckerModalVisible.value = false;
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, 1500));

  try {
    const response = await axiosInstance.get(`/auth/checkInviteHash/${encryptedHash.value}`);
    decryptedHash.value = response.data;
    if(response.data.data.email) {
      lockedEmail.value = true;
    } else {
      lockedEmail.value = false;
    }

    hashCheckerModalVisible.value = false;
  } catch (error: any) {
    hashCheckerModalVisible.value = false;
    errorMessages.value = error.response.data;
    hashNotValidModal.value = true;
  }
};

onMounted(async () => {
  await checkInviteHash();
});

const openMailTo = () => {
  window.open('mailto:airin@tuev-lab.ai');
};

onBeforeUnmount(() => {
  endProgress();
});
</script>
<template>
  <div class="registration-container" v-if="decryptedHash && decryptedHash.valid">
    <div class="surface-card p-5 shadow-2 border-round">
      <ProgressBar v-if="loading" mode="indeterminate" class="mb-5" style="height: 6px"></ProgressBar>

      <div class="text-center mb-5">
        <img :src="assetsUrl + '/logo.svg'" alt="Image" height="50" class="mb-3" />
        <div class="text-900 text-3xl font-medium mb-3">Registration</div>
        <span class="text-muted">Please fill in the form below to register your account.</span>
      </div>

      <div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <InputText disabled v-model="decryptedHash.data.tenantName" inputId="on_label" />
            <label for="on_label">Tenant</label>
          </FloatLabel>
        </div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <InputText disabled v-model="decryptedHash.data.role" inputId="on_label" />
            <label for="on_label">Role</label>
          </FloatLabel>
        </div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <InputText :disabled="lockedEmail" v-model="decryptedHash.data.email" inputId="on_label" />
            <label for="on_label">E-Mail</label>
          </FloatLabel>
        </div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <InputText v-model="firstName" id="firstName" inputId="on_label" />
            <label for="on_label">First Name</label>
          </FloatLabel>
        </div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <InputText v-model="lastName" id="lastName" inputId="on_label" />
            <label for="on_label">Last Name</label>
          </FloatLabel>
        </div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <Password fluid v-model="password" id="password" inputId="on_label" />
            <label for="on_label">Password</label>
          </FloatLabel>
        </div>
        <div class="form-field w-100">
          <FloatLabel variant="on">
            <Password fluid v-model="confirmPassword" :feedback="false" inputId="on_label" />
            <label for="on_label">Confirm Password</label>
          </FloatLabel>
        </div>

        <Button :disabled="registrationDisabled" label="Register" icon="pi pi-check" class="w-full"
          @click="register"></Button>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="successModal" modal :closable="false" header="Success" :style="{ width: '25rem' }">
    <div class="flex items-center gap-4 mb-4">
      <span class="">Registration successful! You will be redirected to the login page shortly to sign in.</span>
    </div>
    <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
  </Dialog>

  <Dialog :draggable="false" v-model:visible="hashCheckerModalVisible" modal :closable="false"
    header="Checking your Link" :style="{ width: '30rem' }">

    <div class="flex items-center gap-4 mb-4">
      <span class="">We are verifying your registration link. Please wait...</span>
    </div>
    <ProgressBar v-if="hashTimer" mode="indeterminate">
      Checking...
    </ProgressBar>
  </Dialog>

  <Dialog :draggable="false" v-model:visible="hashNotValidModal" modal :closable="false"
    header="Invitation Link Error" :style="{ width: '30rem' }">
    <div class="w-100 fluid my-2">
      <Message fluid severity="error">
        <b>Message: </b>{{ errorMessages.message }}
      </Message>

    </div>
    <div class="col-12 d-flex justify-content-end">
        <Button class="p-button-tuev" label="Contact Us" @click="openMailTo">
          <template #icon>
            <font-awesome-icon :icon="['fas', 'envelope']" />
          </template>
        </Button>
      </div>
  </Dialog>

  <div class="background-overlay"></div>

</template>
<style scoped>
.registration-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.background-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100vw;
  height: 100vh;
  background: black !important;
}

.surface-card {
  width: 100%;
  max-width: 500px;
  background: white !important;
  border-radius: 8px;
}

.form-field {
  margin-bottom: 1.5rem;
  width: 100%;
}

.form-field input {
  width: 100% !important;
}

.p-button {
  background-color: var(--tuev-light-blue);
  border-color: var(--tuev-light-blue);
}

.p-button:hover {
  background-color: var(--tuev-dark-blue) !important;
  border-color: var(--tuev-dark-blue) !important;
  color: white;
}
</style>
